import type { ResponseGetConfigCopilotVerdict } from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { routeToUrl } from "@CommonUtils/routes";
import { OMark } from "src/components/PlanogramRules/components/OSummary";
import { Color, Flex, Link, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { pages } from "src/utils/pages";
import { OButton } from "./OParts";

export const O_FIX_PARAM = "o_fix";

export const O_DETAILS_PARAM = "o_details";

export const O_ADJUST_PARAM = "o_adjust";

export const fixUrlOf = (
  merchflowId: number,
  verdict: ResponseGetConfigCopilotVerdict,
): string | null =>
  verdict.fix && verdict.basePogId !== null
    ? routeToUrl(
        pages.merchflows_merchflowId_BasePog_basePogId_,
        { merchflowId, basePogId: verdict.basePogId },
        {
          [O_FIX_PARAM]: verdict.fix.finding,
          store_pog_trigger_id: verdict.lastRun?.storePogTriggerId,
        },
      )
    : null;

export const detailsUrlOf = (
  merchflowId: number,
  verdict: ResponseGetConfigCopilotVerdict,
): string | null =>
  verdict.basePogId !== null &&
  verdict.lastRun !== null &&
  (verdict.status === "done" || verdict.status === "failed")
    ? routeToUrl(
        pages.merchflows_merchflowId_BasePog_basePogId_,
        { merchflowId, basePogId: verdict.basePogId },
        {
          [O_DETAILS_PARAM]: 1,
          store_pog_trigger_id: verdict.lastRun.storePogTriggerId,
        },
      )
    : null;

export const adjustUrlOf = (
  merchflowId: number,
  verdict: ResponseGetConfigCopilotVerdict,
): string | null =>
  verdict.basePogId !== null
    ? routeToUrl(
        pages.merchflows_merchflowId_BasePog_basePogId_,
        { merchflowId, basePogId: verdict.basePogId },
        { [O_ADJUST_PARAM]: 1 },
      )
    : null;

export const isOnAnotherRun = (
  verdict: ResponseGetConfigCopilotVerdict,
  shownRun: number | undefined,
) =>
  verdict.status === "running" &&
  verdict.lastRun !== null &&
  shownRun !== undefined &&
  verdict.lastRun.storePogTriggerId !== shownRun;

export const OVerdict = ({
  merchflowId,
  verdict,
  shownRun,
  isLost = false,
  isAskOpen,
  onAsk,
}: {
  merchflowId: number;

  verdict: ResponseGetConfigCopilotVerdict | null;

  shownRun?: number;

  isLost?: boolean;

  /** Present: an "Ask O" button opens O's questions on this merchflow. */
  isAskOpen?: boolean;
  onAsk?: () => void;
}) => {
  const { t } = useTranslation("common");

  if (!verdict && isLost) {
    return (
      <Flex gap="8px" align="center" data-testid="o-verdict">
        <OMark aria-hidden="true" />
        <Text color={Color.primaryActive}>{t("configCopilot.verdictLost")}</Text>
      </Flex>
    );
  }

  if (!verdict) {
    return (
      <Flex gap="8px" align="center" data-testid="o-verdict-pending">
        <OMark aria-hidden="true" />
        <Text color={Color.textSecondary}>{t("configCopilot.readingRun")}</Text>
      </Flex>
    );
  }

  const fixUrl = fixUrlOf(merchflowId, verdict);
  const adjustUrl = adjustUrlOf(merchflowId, verdict);
  const detailsUrl = detailsUrlOf(merchflowId, verdict);
  const line = isLost
    ? t("configCopilot.verdictLost")
    : isOnAnotherRun(verdict, shownRun) && verdict.lastRun
      ? t("configCopilot.olderRunShown", {
          done: verdict.lastRun.done,
          stores: verdict.lastRun.stores,
        })
      : verdict.line;
  return (
    <Flex gap="8px" align="center" data-testid="o-verdict">
      <OMark aria-hidden="true" />
      <Text color={Color.primaryActive}>{line}</Text>
      <Flex gap="6px" align="center" data-testid="o-verdict-actions">
        {verdict.fix && fixUrl && (
          <Link to={fixUrl}>
            <OButton as="span" isPrimary>
              {verdict.fix.label}
            </OButton>
          </Link>
        )}
        {adjustUrl && (
          <Link to={adjustUrl}>
            <OButton as="span">{t("configCopilot.adjust")}</OButton>
          </Link>
        )}
        {detailsUrl && (
          <Link to={detailsUrl}>
            <OButton as="span">{t("configCopilot.details")}</OButton>
          </Link>
        )}
        {onAsk && (
          <OButton
            type="button"
            aria-expanded={isAskOpen}
            aria-controls="o-ask-drawer"
            data-testid="o-ask-toggle"
            onClick={onAsk}
          >
            {t("configCopilot.askO")}
          </OButton>
        )}
      </Flex>
    </Flex>
  );
};
